import { useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "react-query";
import { useGetAllPuckPages, getAllPuckPagesQueryKey } from "src/api/client-api/cms/useGetAllPuckPages";
import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { useCMSPagesActions, DuplicatePayload } from "./useCMSPagesActions";
import { useCMSPagesTableState } from "./useCMSPagesTableState";
import { categorizePages } from "../components/utils";

export type CreateCMSPagePayload = {
    title: string;
    description?: string;
    allowedRoles: string[];
    url?: string;
    sectors?: string[];
    score?: string[];
    entity?: string[];
};

export type UpdateCMSPageMetaPayload = {
    url?: string;
    sectors?: string[] | null;
    allowedRoles?: string[];
    status?: "active" | "draft";
    title?: string;
    description?: string;
    score?: string | string[];
    entity?: string | string[];
};

export const useCMSPagesManagerController = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
    const [pageToDelete, setPageToDelete] = useState<string | null>(null);

    const PAGE_SIZE = 20;
    const table = useCMSPagesTableState(PAGE_SIZE);

    const {
        pages = [],
        totalItems,
        isLoading,
        isError,
        refetch,
    } = useGetAllPuckPages({
        page: table.pagination.pageIndex + 1,
        itemsPerPage: table.pagination.pageSize,
        search: table.filters.title,
        pageType: "CMS",
        orderBy: Object.keys(table.orderBy).length > 0 ? table.orderBy : { createdAt: "desc" },
        url: table.filters.url || undefined,
        sectorId: table.filters.sectorId,
        status: table.filters.status,
    });

    const actions = useCMSPagesActions(pages, refetch, "CMS");

    const navigateToEditor = (slug: string, isNewlyCreated = false) => {
        const newFlag = isNewlyCreated ? "&new=true" : "";
        navigate(`/settings/cms/${slug}?t=${Date.now()}${newFlag}`);
    };

    const handleDuplicatePage = async (source: PuckPage, payload: DuplicatePayload) => {
        try {
            setIsCreating(true);
            const newPage = await actions.duplicatePage(source, payload);
            queryClient.invalidateQueries(["puckPage", newPage.slug]);
            queryClient.invalidateQueries([getAllPuckPagesQueryKey]);
            await refetch();
        } catch (e) {
            console.error("Duplicate page failed", e);
        } finally {
            setIsCreating(false);
        }
    };

    const handleUpdateMeta = async (slug: string, payload: UpdateCMSPageMetaPayload) => {
        try {
            return await actions.updateMeta(slug, {
                title: payload.title,
                description: payload.description,
                allowedRoles: payload.allowedRoles,
                status: payload.status,
                url: payload.url,
                sectors: payload.sectors ?? undefined,
                score: payload.score,
                entity: payload.entity,
            });
        } catch (error) {
            return console.error("Error updating page meta:", error);
        }
    };

    const handleCreatePage = async (payload: CreateCMSPagePayload) => {
        setIsCreating(true);

        const initialContent = {
            content: [],
            root: {
                props: {
                    title: payload.title,
                    description: "",
                    pageType: "CMS",
                },
            },
            roles: {
                allowedRoles: payload.allowedRoles,
            },
        };

        try {
            const result = await actions.createPage({
                title: payload.title,
                content: JSON.stringify(initialContent),
                allowedRoles: payload.allowedRoles,
                url: payload.url,
                sectors: payload.sectors,
                status: "draft",
                pageType: "CMS",
            });
            queryClient.invalidateQueries([getAllPuckPagesQueryKey]);
            queryClient.invalidateQueries(["puckPage", result.slug]);
            queryClient.refetchQueries([getAllPuckPagesQueryKey]);
            await refetch();
        } catch (error) {
            console.error("Error creating page:", error);
        } finally {
            setIsCreateDialogOpen(false);
            setIsCreating(false);
        }
    };

    const confirmDelete = (slug: string) => {
        setPageToDelete(slug);
        setDeleteConfirmDialogOpen(true);
    };

    const handleDeletePage = () => {
        if (!pageToDelete) return;

        actions
            .deletePage(pageToDelete)
            .then(async () => {
                setDeleteConfirmDialogOpen(false);
                setPageToDelete(null);
            })
            .catch(() => {});
    };

    const handleUpdateTitle = async (slug: string, title: string) => {
        try {
            await actions.updateTitle(slug, title);
        } catch (error) {
            console.error("Failed to update title:", error);
        }
    };

    const handleUpdateStatus = (slug: string, status: "active" | "draft") => {
        actions.updateStatus(slug, status).catch((error) => console.error("Error updating status:", error));
    };

    const handleUpdatePageRoles = (slug: string, allowedRoles: string[]) => {
        actions.updateRoles(slug, allowedRoles).catch((error) => console.error("Error updating page roles:", error));
    };

    const handleCreateHomePage = async () => {
        setIsCreating(true);

        const initialContent = {
            content: [],
            root: {
                props: {
                    title: "home",
                    description: "",
                    pageType: "CMS",
                },
            },
            roles: {
                allowedRoles: [],
            },
        };

        try {
            await actions.createPage({
                title: "home",
                content: JSON.stringify(initialContent),
                slug: "home",
                status: "draft",
                url: "/",
                pageType: "CMS",
            });
            queryClient.invalidateQueries(["puckPage", "home"]);
            queryClient.invalidateQueries([getAllPuckPagesQueryKey]);
            await refetch();
        } catch (error) {
            console.error("Error creating home page:", error);
        } finally {
            setIsCreating(false);
        }
    };

    const { homePage: homePageArray, regularPages } = categorizePages(pages);
    const homePage = homePageArray.length > 0 ? homePageArray[0] : null;

    return {
        isLoading,
        isError,
        totalItems,
        pages: [...homePageArray, ...regularPages],
        homePageExists: !!homePage,
        table,
        actions,
        isCreating,
        isCreateDialogOpen,
        openCreateDialog: () => setIsCreateDialogOpen(true),
        closeCreateDialog: () => setIsCreateDialogOpen(false),
        handleCreatePage,
        handleCreateHomePage,
        navigateToEditor,
        handleDuplicatePage,
        handleUpdateTitle,
        handleUpdateMeta,
        handleUpdateStatus,
        handleUpdatePageRoles,
        confirmDelete,
        deleteConfirmDialogOpen,
        closeDeleteConfirmDialog: () => {
            setDeleteConfirmDialogOpen(false);
            setPageToDelete(null);
        },
        handleDeletePage,
        pageToDelete,
    };
};
